/**
 * PATH: packages/pricelist/src/formulaFields.ts
 * FILE_ID: PKG-PRICELIST-FORMULAFIELDS-001
 * PURPOSE: Pure helper listing the fields a column formula may reference —
 *          the numeric core fields (Cost, Markup %) plus the section's other
 *          columns — so the column editor can offer a click-to-insert field
 *          picker instead of operators typing raw keys blind.
 * WHY: Operators asked for "rules on the fields they connect to in formulas".
 *      A formula like "=base_cost * (1 + markup_percent/100)" must reference
 *      real keys; surfacing them removes guesswork + typos.
 * RELATED: ColumnDialog.tsx (consumer), columns.ts (CORE refs), types.ts
 */
import type { ColumnDef } from './types'

export interface FormulaField {
  key: string
  label: string
}

/** Numeric core fields every row carries and a formula can reference. */
const CORE_FORMULA_FIELDS: FormulaField[] = [
  { key: 'base_cost', label: 'Cost' },
  { key: 'markup_percent', label: 'Markup %' },
]

/**
 * Fields a column formula can reference: numeric core fields + the section's
 * other columns. `excludeKey` drops the column currently being edited so a
 * formula can't reference itself.
 */
export function availableFormulaFields(
  schema: ColumnDef[],
  excludeKey?: string,
): FormulaField[] {
  const seen = new Set(CORE_FORMULA_FIELDS.map((f) => f.key))
  const fields = [...CORE_FORMULA_FIELDS]
  for (const col of schema) {
    if (!col.key || col.key === excludeKey || seen.has(col.key)) continue
    seen.add(col.key)
    fields.push({ key: col.key, label: col.label || col.key })
  }
  return fields
}
